import * as React from "react";
import "./style.css";
import { GeneralLoaderProvider } from "./components/Common/GeneralLoader/GeneralLoaderProvider";
import { UserProvider } from "./components/Contexts/UserContext";
import { CreateTransferFlow } from "./components/CreateTransferFlow/CreateTransferFlow";
import { AddRecipientFlow } from "./components/AddRecipientFlow/AddRecipientFlow";
import { EditBeneficiaryFlow } from "./components/EditBeneficiaryFlow/EditBeneficiaryFlow";
import ReactDOM from "react-dom/client";
import { ScaVerify } from "./components/ScaAirwallex/ScaVerify";
import { ViewPin } from "./components/ViewPinFlow/ViewPin";
import { ViewFullCardDetails } from "./components/ViewFullCardDetails/ViewFullCardDetails";
import { AuthoriseBankTransfer } from "./components/AuthoriseBankTransfer/AuthoriseBankTransfer";
import { completeTransfer, initiateTransfer } from "./utils/scaHelper";
import { ScaVerifyModal } from "./components/ScaVerifyModal/ScaVerifyModal";
import { ScaVerifyEmbedded } from "./components/ScaVerifyEmbedded/ScaVerifyEmbedded";
import { SCA_VERIFY_EVENTS } from "./components/ScaVerifyEmbedded/scaVerifyEvents";

let reactRoot = null;
let iosRoot = null;

function renderComponent(Component) {
  const container = document.getElementById("react-wrapper");

  // Create root once
  if (!reactRoot) {
    reactRoot = ReactDOM.createRoot(container);
  }

  reactRoot.render(
    <GeneralLoaderProvider>
      <UserProvider>{Component}</UserProvider>
    </GeneralLoaderProvider>
  );
}

/**
 * Close any open Drupal/jQuery UI dialog (e.g. the Review modal that contains Approve/Deny).
 */
function closeDrupalDialog() {
  if (typeof jQuery !== "undefined") {
    jQuery("button.ui-dialog-titlebar-close").click();
  }
}

window.initCreateTransferFlow = function () {
  renderComponent(<CreateTransferFlow />);
};

window.initAddRecipientFlow = function () {
  renderComponent(<AddRecipientFlow />);
};

window.initEditBeneficiaryFlow = function (beneficiaryId) {
  renderComponent(<EditBeneficiaryFlow beneficiaryId={Number(beneficiaryId)} />);
};

window.initViewPinFlow = function () {
  renderComponent(<ViewPin />);
};

/**
 * Open Authorise or Deny bank transfer flow.
 * @param {number} orderId - The naut_order id.
 * @param {string} [mode='authorise'] - 'authorise' (confirm + approve, with optional SCA) or 'deny'.
 */
window.initAuthoriseBankTransfer = function (orderId, mode = "authorise") {
  renderComponent(<AuthoriseBankTransfer orderId={Number(orderId)} mode={mode} />);
};

window.initScaVerify = function ({
  scaSessionCode,
  onVerificationSucceed,
  onVerificationFailed,
  onError,
  onCancel,
  onReady,
}) {
  renderComponent(
    <ScaVerifyModal
      scaSessionCode={scaSessionCode}
      onReady={onReady}
      onVerificationSucceed={onVerificationSucceed}
      onVerificationFailed={onVerificationFailed}
      onError={onError}
      onCancel={onCancel}
    />
  );
};

/**
 * Event names for ScaVerify embedded – use when adding listeners in the APP.
 * Example: window.addEventListener(window.ScaVerifyEvents.SUCCESS, (e) => { ... e.detail });
 */
window.ScaVerifyEvents = SCA_VERIFY_EVENTS;

/**
 * Mount ScaVerify embedded in a specific container for APP flows.
 * The component will dispatch success/error events on window; the APP can listen
 * via window.addEventListener(ScaVerifyEvents.SUCCESS, ...) etc.
 *
 * @param {Object} options
 * @param {string} [options.containerId='react-wrapper'] - ID of the DOM element to mount into
 * @param {string} [options.scaSessionCode] - Optional SCA session code for verification
 * @param {Function} [options.onVerificationSucceed] - Callback (optional, events still emitted)
 * @param {Function} [options.onVerificationFailed] - Callback (optional)
 * @param {Function} [options.onError] - Callback (optional)
 * @param {Function} [options.onCancel] - Callback (optional)
 * @param {Function} [options.onReady] - Callback (optional)
 */
let scaEmbeddedRoots = {};

window.initScaVerifyEmbedded = function (options = {}) {
  const {
    containerId = "react-wrapper",
    scaSessionCode,
    onVerificationSucceed,
    onVerificationFailed,
  } = options;

  const container = document.getElementById(containerId);
  if (!container) {
    console.warn("ScaVerifyEmbedded: container not found:", containerId);
    return;
  }

  if (!scaEmbeddedRoots[containerId]) {
    scaEmbeddedRoots[containerId] = ReactDOM.createRoot(container);
  }

  scaEmbeddedRoots[containerId].render(
    <GeneralLoaderProvider>
      <UserProvider>
        <ScaVerifyEmbedded
          scaSessionCode={scaSessionCode}
          onVerificationSucceed={onVerificationSucceed}
          onVerificationFailed={onVerificationFailed}
          onError={options.onError}
          onCancel={options.onCancel}
          onReady={options.onReady}
          onScaSetupSucceed={options.onScaSetupSucceed}
        />
      </UserProvider>
    </GeneralLoaderProvider>
  );
};

window.initScaSetup = function(props) {
  const container = document.getElementById("sca-setup-component");
  if (!container) return;

  if (!window.scaRoot) {
    window.scaRoot = ReactDOM.createRoot(container);
  }

  window.scaRoot.render(
    <GeneralLoaderProvider>
      <UserProvider>
        <ScaVerify {...props} />
      </UserProvider>
    </GeneralLoaderProvider>
  );
};

window.destoryReactApp = function () {
  if (reactRoot) {
    reactRoot.unmount();
    reactRoot = null; // allow remounting next time
  }
};

// Add a single event listener to the document
document.addEventListener("click", function (event) {
  // Get the element that was clicked
  const clickedElement = event.target;

  // Check if the clicked element has one of the specified classes
  if (clickedElement.classList.contains("create-transfer-link")) {
    event.preventDefault();
    renderComponent(<CreateTransferFlow />);
  }
  else if (clickedElement.classList.contains("add-recipient-link")) {
    event.preventDefault();
    renderComponent(<AddRecipientFlow />);
  }
  else if (clickedElement.classList.contains("edit-beneficiary-link")) {
    event.preventDefault();
    const beneficiaryId = clickedElement.getAttribute("data-beneficiary-id");
    if (beneficiaryId) {
      closeDrupalDialog();
      renderComponent(<EditBeneficiaryFlow beneficiaryId={Number(beneficiaryId)} />);
    }
  }
  else if (clickedElement.classList.contains("view-pin-link")) {
    event.preventDefault();
    renderComponent(<ViewPin />);
  }
  else if (clickedElement.classList.contains("view-full-card-details-link")) {
    event.preventDefault();
    renderComponent(<ViewFullCardDetails />);
  }
  else if (clickedElement.classList.contains("authorise-bank-transfer-link")) {
    event.preventDefault();
    const orderId = clickedElement.getAttribute("data-order-id");
    if (orderId) {
      closeDrupalDialog();
      renderComponent(<AuthoriseBankTransfer orderId={Number(orderId)} mode="authorise" />);
    }
  }
  else if (clickedElement.classList.contains("deny-bank-transfer-link")) {
    event.preventDefault();
    const orderId = clickedElement.getAttribute("data-order-id");
    if (orderId) {
      closeDrupalDialog();
      renderComponent(<AuthoriseBankTransfer orderId={Number(orderId)} mode="deny" />);
    }
  }
});

if (document.getElementById("ios-embedded-component")) {
  const container = document.getElementById("ios-embedded-component");

  if (!iosRoot) {
    iosRoot = ReactDOM.createRoot(container);
  }

  iosRoot.render(
    <GeneralLoaderProvider>
      <UserProvider>
        <CreateTransferFlow />
      </UserProvider>
    </GeneralLoaderProvider>
  );
}

// Standalone SCA verify route (e.g. /airwallex/sca-verify) for WebView apps.
// Mounts ScaVerifyEmbedded and emits success/error events on window for native apps to listen.
if (document.getElementById("sca-verify-webview")) {
  const scaSessionCode =
    window.drupalSettings?.scaVerifyWebView?.scaSessionCode || null;
  window.initScaVerifyEmbedded({
    containerId: "sca-verify-webview",
    scaSessionCode: scaSessionCode || undefined,
  });
}

window.startConnectedAccountTransfer = async function (payload) {
  try {
    if (!payload) {
      throw new Error("Payload is required");
    }

    console.log("🚀 Initiating transfer with payload:", payload);

    // Step 1: Trigger SCA
    const scaSessionCode = await initiateTransfer(payload);

    console.log("🔐 Received SCA session code:", scaSessionCode);

    // Step 2: Launch React SCA UI
    window.initScaVerify({
      scaSessionCode,

      onVerificationSucceed: async ({ token }) => {
        console.log("✅ SCA token received:", token);

        try {
          const result = await completeTransfer(payload, token);
          console.log("🎉 Transfer successful:", result);
        } catch (err) {
          console.error("❌ Transfer failed after SCA:", err);
        }
      },

      onVerificationFailed: ({ reason }) => {
        console.error("❌ SCA verification failed:", reason);
      },

      onCancel: () => {
        console.warn("⚠️ User cancelled SCA");
      },

      onError: (err) => {
        console.error("❌ SCA error:", err);
      },
    });
  } catch (err) {
    console.error("🔥 Transfer flow failed:", err);
  }
};

window.scaFetch = async (url, requestOptions = {}) => {
  const {
    method = "GET",
    headers = {},
    body,
    redirect,
  } = requestOptions;

  // Normalize headers (support Headers, object, Map)
  const normalizedHeaders = headers instanceof Headers
    ? Object.fromEntries(headers.entries())
    : headers;

  // First request (NO SCA token)
  const res = await fetch(url, {
    method,
    headers: normalizedHeaders,
    body,
    redirect,
  });

  // 🔐 SCA challenge detected
  if (res.status === 403) {
    const scaSessionCode = res.headers.get("x-sca-session-code");

    if (!scaSessionCode) {
      throw new Error("403 received but no x-sca-session-code");
    }

    return new Promise((resolve, reject) => {
      window.initScaVerify({
        scaSessionCode,

        onVerificationSucceed: async ({ token }) => {
          try {
            const retryHeaders = {
              ...normalizedHeaders,
              "x-sca-token": token,
            };

            const retryRes = await fetch(url, {
              method,
              headers: retryHeaders,
              body,
              redirect,
            });

            if (!retryRes.ok) {
              const err = await retryRes.text();
              reject(err);
            } else {
              resolve(retryRes);
            }
          } catch (e) {
            reject(e);
          }
        },

        onCancel: () => {
          reject(new Error("User cancelled SCA"));
        },

        onError: reject,
      });
    });
  }

  // ✅ Normal success
  return res;
}
